import type { ReactNode } from 'react'
import { groupBy } from 'lodash-es'
import { format, isBefore, startOfDay } from 'date-fns'
import { SectionHeadline } from '@/components/SectionHeadline'
import styles from './Deadlines.module.css'

interface DeadlineDate {
  year: number
  month: number
  day: number
}

export interface Deadline {
  date: DeadlineDate
  description: string
}

interface DeadlinesContainerProps {
  deadlines: Deadline[]
}

/**
 * The DeadlinesContainer
 */
export function DeadlinesContainer({ deadlines }: DeadlinesContainerProps) {
  const today = startOfDay(new Date())

  // Filter only the upcoming deadlines.
  const upcomingDeadlines = deadlines.filter((deadline) => {
    const deadlineDate = new Date(
      deadline.date.year,
      deadline.date.month - 1,
      deadline.date.day,
    )
    return !isBefore(deadlineDate, today)
  })

  if (upcomingDeadlines.length === 0) {
    return null
  }

  const groupedByYearMonthDay =
    groupDeadlinesByYearMonthAndDay(upcomingDeadlines)

  return (
    <Deadlines headline="Upcoming tax deadlines">
      {Object.entries(groupedByYearMonthDay).map(([yearMonth, deadlines]) => {
        const [yearValue, monthValue] = yearMonth.split('-')

        const year = parseInt(yearValue)
        const month = parseInt(monthValue)
        const monthName = getMonthName(year, month)

        return (
          <DeadlineMonth key={yearMonth} title={`${monthName} ${year}`}>
            {deadlines.map((deadline, index) =>
              index === 0 ? (
                <DeadlineItemHeader
                  key={index}
                  day={getShortDayName(year, month, deadline.date.day)}
                  date={deadline.date.day}
                >
                  {deadline.description}
                </DeadlineItemHeader>
              ) : (
                <DeadlineItemRow key={index}>
                  {deadline.description}
                </DeadlineItemRow>
              ),
            )}
          </DeadlineMonth>
        )
      })}
    </Deadlines>
  )
}

interface DeadlinesProps {
  headline?: string
  children?: ReactNode
  testId?: string
}

/**
 * The Deadlines
 */
export function Deadlines({ headline, children, testId }: DeadlinesProps) {
  return (
    <div className={styles.root} data-test={testId}>
      {headline && <SectionHeadline>{headline}</SectionHeadline>}
      <ol className={styles.deadlines}>{children}</ol>
    </div>
  )
}

interface DeadlineProps {
  title: string
  children?: ReactNode
  testId?: string
}

export function DeadlineMonth({ title, children, testId }: DeadlineProps) {
  return (
    <li data-test={testId}>
      <div className="text-body-label">{title}</div>
      <ul className="flex gap-sm flex-col">{children}</ul>
    </li>
  )
}

interface DeadlineItemRowProps {
  date?: string
  day?: string
  children?: ReactNode
  testId?: string
}

export function DeadlineItemRow({ children, testId }: DeadlineItemRowProps) {
  return (
    <li className={styles.deadlineItem} data-test={testId}>
      <div className={styles.spacer}></div>
      <div className={styles.deadlineItemDescription}>{children}</div>
    </li>
  )
}

interface DeadlineItemHeaderProps {
  date: number
  day: string
  children?: ReactNode
  testId?: string
}

export function DeadlineItemHeader({
  day,
  date,
  children,
  testId,
}: DeadlineItemHeaderProps) {
  return (
    <li className={styles.deadlineItem} data-test={testId}>
      <div className={styles.deadlineItemDate}>
        <p className={styles.day}>{day}</p>
        <p className={styles.date}>{date}</p>
      </div>
      <div className={styles.deadlineItemDescription}>{children}</div>
    </li>
  )
}

/**
 * Group deadlines by year, month and day.
 */
function groupDeadlinesByYearMonthAndDay(upcomingDeadlines: Deadline[]) {
  const sortedDeadlines = upcomingDeadlines.sort((a, b) => {
    return (
      a.date.year - b.date.year ||
      a.date.month - b.date.month ||
      a.date.day - b.date.day
    )
  })

  return groupBy(
    sortedDeadlines,
    (item) => `${item.date.year}-${item.date.month}-${item.date.day}`,
  )
}

/**
 * Gets the short day name.
 */
export function getShortDayName(year: number, month: number, day: number) {
  return format(new Date(year, month - 1, day), 'EEE')
}

/**
 * Gets the month name.
 */
export function getMonthName(year: number, month: number) {
  return format(new Date(year, month - 1, 1), 'MMMM')
}
